import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Link2Off, Loader2, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { ScanVragenlijst, ScanVraag, ScanAntwoorden, scanCompleet } from '../components/ScanVragenlijst';

interface Scan {
  titel: string;
  eind_op: string;
  bedrijfsnaam: string | null;
  vragen: ScanVraag[];
}

const sleutel = (token: string) => `belagom-scan-klaar:${token}`;
const leesVinkje = (token: string) => {
  try {
    return sessionStorage.getItem(sleutel(token)) === '1';
  } catch {
    return false;
  }
};
const zetVinkje = (token: string) => {
  try {
    sessionStorage.setItem(sleutel(token), '1');
  } catch {
    /* privémodus: dan valt een reload terug op de "link niet meer geldig"-melding */
  }
};

const Schil: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-8">
    <div className="mx-auto max-w-xl">
      {children}
    </div>
  </div>
);

/**
 * Publieke Bedrijfsthermometer-scan via de unieke link uit e-mail/SMS. Geen
 * login. Na verzenden is de token server-side vernietigd; het vinkje in
 * sessionStorage zorgt dat een reload het bedankscherm toont in plaats van
 * een foutmelding (idempotency-randgeval uit de spec).
 */
export const PulseScanPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const { token = '' } = useParams<{ token: string }>();
  const [klaar, setKlaar] = useState(() => leesVinkje(token));
  const [antwoorden, setAntwoorden] = useState<ScanAntwoorden>({});

  const scan = useQuery({
    queryKey: ['pulse-scan', token],
    queryFn: async () => (await api.get<{ data: Scan }>(`/pulse-scan/${encodeURIComponent(token)}`)).data.data,
    enabled: !klaar,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const verzend = useMutation({
    mutationFn: () => api.post(`/pulse-scan/${encodeURIComponent(token)}`, { antwoorden }),
    onSuccess: () => {
      zetVinkje(token);
      setKlaar(true);
    },
  });

  if (klaar) {
    return (
      <Schil>
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm" role="status">
          <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" aria-hidden />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">{t('scan.bedankt', { defaultValue: 'Bedankt voor je eerlijke mening!' })}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('scan.bedanktUitleg', { defaultValue: 'Je antwoorden zijn anoniem opgeslagen en je unieke token is vernietigd. Niemand kan je antwoorden naar jou herleiden.' })}
          </p>
        </div>
      </Schil>
    );
  }

  if (scan.isLoading) {
    return (
      <Schil>
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0B1F3A]" aria-label={t('scan.laden', { defaultValue: 'Laden…' })} /></div>
      </Schil>
    );
  }

  if (scan.isError || !scan.data) {
    const melding = (scan.error as any)?.response?.data?.message;
    return (
      <Schil>
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm" role="alert">
          <Link2Off className="mx-auto h-12 w-12 text-gray-400" aria-hidden />
          <h1 className="mt-4 text-xl font-bold text-gray-900">{t('scan.ongeldigTitel', { defaultValue: 'Deze link is niet (meer) geldig' })}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {melding ?? t('scan.ongeldig', { defaultValue: 'Heb je de scan al ingevuld? Dan is je unieke token direct vernietigd ter bescherming van je privacy.' })}
          </p>
        </div>
      </Schil>
    );
  }

  const s = scan.data;
  const compleet = scanCompleet(s.vragen, antwoorden);
  const fout = (verzend.error as any)?.response?.data?.message;
  return (
    <Schil>
      <div className="rounded-2xl bg-white p-6 shadow-sm sm:p-8">
        <h1 className="text-2xl font-bold text-gray-900">{s.titel}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {s.bedrijfsnaam && `${s.bedrijfsnaam} · `}
          {t('peiling.openTot', { defaultValue: 'Open tot {{datum}}', datum: new Date(s.eind_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' }) })}
        </p>
        <div className="mt-4 flex gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-900">
          <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden />
          <p>{t('scan.privacy', { defaultValue: 'Volledig anoniem: je antwoorden worden losgekoppeld van je naam en e-mailadres. Resultaten per afdeling zijn pas zichtbaar vanaf 10 deelnemers.' })}</p>
        </div>
        <form
          className="mt-6 space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (compleet) verzend.mutate();
          }}
        >
          <ScanVragenlijst vragen={s.vragen} antwoorden={antwoorden} zet={(id, w) => setAntwoorden((a) => ({ ...a, [id]: w }))} />
          {fout && <p className="text-sm text-red-600" role="alert">{fout}</p>}
          <button
            type="submit"
            disabled={!compleet || verzend.isPending}
            className="w-full rounded-lg bg-[#0B1F3A] px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
          >
            {verzend.isPending ? t('scan.bezig', { defaultValue: 'Versturen…' }) : t('thermometer.versturen', { defaultValue: 'Anoniem versturen' })}
          </button>
        </form>
      </div>
    </Schil>
  );
};

export default PulseScanPage;
